import React, { useState, useEffect } from 'react';
import './Cart.css';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';
import { getApiUrl } from '../config';

const Cart = () => {
  const [cartItem, setCartItem] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  const [showApartmentField, setShowApartmentField] = useState(false);
  const [showOrderNote, setShowOrderNote] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);

  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');

    const item = localStorage.getItem('cartItem');
    if (item) {
      setCartItem(JSON.parse(item));
    }

    // Handle redirect back from Stripe
    const query = new URLSearchParams(window.location.search);
    if (query.get('success')) {
      const pendingForm = localStorage.getItem('pendingOrderForm');
      if (pendingForm) {
        const parsedForm = JSON.parse(pendingForm);
        const receiptNo = `AQL-${Date.now().toString().slice(-6)}`;
        const orderWithReceipt = {
          ...parsedForm,
          receiptNo
        };
        
        submitForm('Cart Checkout Form', orderWithReceipt)
          .then(() => {
            setCompletedOrder(orderWithReceipt);
            setIsSuccess(true);
            localStorage.removeItem('cartItem');
            localStorage.removeItem('pendingOrderForm');
            window.history.replaceState({}, document.title, window.location.pathname);
          })
          .catch((err) => {
            console.error('Error submitting order form after checkout success:', err);
            setCompletedOrder(orderWithReceipt);
            setIsSuccess(true);
            localStorage.removeItem('cartItem');
            localStorage.removeItem('pendingOrderForm');
            window.history.replaceState({}, document.title, window.location.pathname);
          });
      } else {
        const receiptNo = `AQL-${Date.now().toString().slice(-6)}`;
        setCompletedOrder({
          packageName: 'Quran Class Package',
          totalAmount: 35,
          packagePrice: 35,
          quantity: 1,
          email: '',
          firstName: 'Valued Customer',
          lastName: '',
          address: '',
          city: '',
          state: '',
          zip: '',
          receiptNo
        });
        setIsSuccess(true);
        localStorage.removeItem('cartItem');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (query.get('canceled')) {
      Swal.fire({
        title: 'Payment Canceled',
        text: 'Your payment process was canceled. Please try again when you are ready.',
        icon: 'info',
        confirmButtonColor: '#5c9456'
      }).then(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      });
    }
  }, []);

  useEffect(() => {
    if (isSuccess && completedOrder) {
      const timer = setTimeout(() => {
        downloadReceipt();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, completedOrder]);

  const downloadReceipt = () => {
    const element = document.getElementById('receipt-downloadable-invoice');
    if (!element) return;
    
    element.style.display = 'block';

    const opt = {
      margin:       10,
      filename:     `Receipt_${completedOrder?.receiptNo || 'Order'}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    const executePdf = () => {
      window.html2pdf().from(element).set(opt).save().then(() => {
        element.style.display = 'none';
      });
    };

    if (!window.html2pdf) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = executePdf;
      document.body.appendChild(script);
    } else {
      executePdf();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRemove = () => {
    localStorage.removeItem('cartItem');
    setCartItem(null);
  };

  const handleUpdateQuantity = (newQty) => {
    if (newQty < 1) return;
    const updated = {
      ...cartItem,
      quantity: newQty
    };
    setCartItem(updated);
    localStorage.setItem('cartItem', JSON.stringify(updated));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const priceVal = cartItem?.priceVal || 0;
    const quantity = cartItem?.quantity || 1;
    const totalAmount = priceVal * quantity;
    
    // Show a loading alert
    Swal.fire({
      title: 'Redirecting to checkout...',
      text: 'Please wait while we secure your session.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const orderData = {
        ...formData,
        packageName: cartItem?.name || cartItem?.title || 'Quran Class Package',
        packagePrice: priceVal,
        quantity,
        totalAmount
      };
      
      // Store form data to submit after checkout success
      localStorage.setItem('pendingOrderForm', JSON.stringify(orderData));

      // Call backend to create checkout session
      const response = await fetch(getApiUrl('/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: cartItem?.name || 'Quran Class Package',
          priceVal: priceVal,
          quantity: quantity,
          customerEmail: formData.email,
          successUrl: `${window.location.origin}/cart?success=true`,
          cancelUrl: `${window.location.origin}/cart?canceled=true`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create stripe session');
      }

      const session = await response.json();
      if (session.url) {
        window.location.href = session.url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to process checkout. Please try again.',
        icon: 'error',
        confirmButtonColor: '#5c9456'
      });
    }
  };

  return (
    <div className="cart-page">
      {/* Sub Hero Banner */}
      <section
        className="cart-hero"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/home-banner.jpg')`
        }}
      >
        <div className="cart-hero__container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h1 className="cart-hero__title" style={{ marginBottom: '8px' }}>Shopping Cart</h1>
          <nav className="about-hero__breadcrumb" aria-label="breadcrumb">
            <a href="/" className="about-hero__breadcrumb-link">
              Home
            </a>
            <span className="about-hero__breadcrumb-separator">&gt;</span>
            <span className="about-hero__breadcrumb-current">Shopping Cart</span>
          </nav>
        </div>
      </section>

      {/* Cart Content */}
      <section className="cart-content-section">
        <div className="cart-content__container">
          {isSuccess && completedOrder ? (
            <div className="thank-you-container" style={{ textAlign: 'center', padding: '60px 20px', maxWidth: '800px', margin: '0 auto', background: '#fff', borderRadius: '12px', boxProfile: '0 4px 15px rgba(0,0,0,0.05)' }}>
               <div style={{ fontSize: '72px', color: '#5c9456', marginBottom: '20px' }}>✓</div>
               <h2 style={{ fontSize: '32px', color: '#111827', fontWeight: 'bold', marginBottom: '16px' }}>Thank You for Your Order!</h2>
               <p style={{ fontSize: '18px', color: '#4b5563', marginBottom: '32px', lineHeight: '1.6' }}>
                 Your payment of <strong style={{ color: '#5c9456' }}>${completedOrder.totalAmount}</strong> has been processed successfully. 
                 A receipt and details have been sent to your email <strong>{completedOrder.email}</strong>.
               </p>
               <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                 <button onClick={downloadReceipt} className="cart-checkout-submit-btn" style={{ maxWidth: '260px', background: '#5c9456', margin: 0, height: '50px', padding: '0 24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                   Download Receipt PDF
                 </button>
                 <a href="/" className="cart-empty__btn" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0, textDecoration: 'none', height: '50px', padding: '0 24px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '16px', fontWeight: '700', boxSizing: 'border-box' }}>
                   Back to Home
                 </a>
               </div>
            </div>
          ) : !cartItem ? (
            <div className="cart-empty">
              <div className="cart-empty__icon-wrapper">
                <svg className="cart-empty__icon" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.116 60.116 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                </svg>
              </div>
              <h2 className="cart-empty__title">Your Cart is Empty</h2>
              <p className="cart-empty__desc">You haven't added any packages to your cart yet.</p>
              <a href="/pay-fee-online/" className="cart-empty__btn">
                Browse Packages
              </a>
            </div>
          ) : (
            <div className="cart-grid">
              {/* Left Column: Product Info */}
              <div className="cart-left-col">
                <h2 className="cart-section-title">Selected Package</h2>
                <div className="cart-item-card">
                  <div className="cart-item-card__header">
                    <h3 className="cart-item-card__title">{cartItem.name}</h3>
                    <button className="cart-item-card__remove" onClick={handleRemove} title="Remove Package" aria-label="Remove Package">
                      <svg className="cart-item-card__remove-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>

                  <div className="cart-item-card__price-row">
                    <span className="cart-item-card__price-val">{cartItem.price}</span>
                    <span className="cart-item-card__original-val">{cartItem.original}</span>
                  </div>

                  <div className="cart-item-card__quantity-row" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '24px', backgroundColor: '#f9fafb', padding: '15px 20px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                    <span className="cart-item-card__quantity-label" style={{ fontWeight: '600', fontSize: '15px', color: '#374151' }}>Quantity:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        type="button"
                        onClick={() => handleUpdateQuantity(cartItem.quantity - 1)}
                        disabled={cartItem.quantity <= 1}
                        style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d1d5db', background: '#ffffff', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', color: '#111111' }}
                      >
                        -
                      </button>
                      <span style={{ fontSize: '16px', fontWeight: '700', minWidth: '24px', textAlign: 'center', color: '#111111' }}>{cartItem.quantity}</span>
                      <button
                        type="button"
                        onClick={() => handleUpdateQuantity(cartItem.quantity + 1)}
                        style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d1d5db', background: '#ffffff', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', color: '#111111' }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="cart-item-card__features-title">Package Inclusions:</div>
                  <ul className="cart-item-card__features-list">
                    {cartItem.features.map((feature, idx) => (
                      <li key={idx} className="cart-item-card__feature-item">
                        <svg className="cart-item-card__check-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Column: Checkout Form */}
              <div className="cart-right-col">
                <form className="cart-checkout-form" onSubmit={handleSubmit}>
                  {/* Contact Information */}
                  <div className="cart-form-section">
                    <h3 className="cart-form-section-title">Contact information</h3>
                    <div className="cart-form-group">
                      <input
                        type="email"
                        name="email"
                        placeholder="Email address"
                        required
                        className="cart-form-input"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                      <span className="cart-form-subtext">You are currently checking out as a guest.</span>
                    </div>
                  </div>

                  {/* Billing Address */}
                  <div className="cart-form-section">
                    <h3 className="cart-form-section-title">Billing address</h3>
                    <div className="cart-form-group">
                      <select name="country" className="cart-form-input cart-form-select">
                        <option value="US">United States (US)</option>
                        <option value="CA">Canada (CA)</option>
                        <option value="UK">United Kingdom (UK)</option>
                        <option value="AU">Australia (AU)</option>
                      </select>
                    </div>

                    <div className="cart-form-group-row">
                      <div className="cart-form-group">
                        <input
                          type="text"
                          name="firstName"
                          placeholder="First name"
                          required
                          className="cart-form-input"
                          value={formData.firstName}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="cart-form-group">
                        <input
                          type="text"
                          name="lastName"
                          placeholder="Last name"
                          required
                          className="cart-form-input"
                          value={formData.lastName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="cart-form-group">
                      <input
                        type="text"
                        name="address"
                        placeholder="Address"
                        required
                        className="cart-form-input"
                        value={formData.address}
                        onChange={handleInputChange}
                      />
                      {!showApartmentField ? (
                        <span className="cart-form-link-text" onClick={() => setShowApartmentField(true)}>
                          + Add apartment, suite, etc.
                        </span>
                      ) : (
                        <div style={{ marginTop: '12px' }}>
                          <input
                            type="text"
                            name="apartment"
                            placeholder="Apartment, suite, etc. (optional)"
                            className="cart-form-input"
                            value={formData.apartment || ''}
                            onChange={handleInputChange}
                          />
                        </div>
                      )}
                    </div>

                    <div className="cart-form-group-row">
                      <div className="cart-form-group">
                        <input
                          type="text"
                          name="city"
                          placeholder="City"
                          required
                          className="cart-form-input"
                          value={formData.city}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="cart-form-group">
                        <select name="state" className="cart-form-input cart-form-select" value={formData.state || 'NY'} onChange={handleInputChange}>
                          <option value="NY">New York</option>
                          <option value="CA">California</option>
                          <option value="TX">Texas</option>
                          <option value="FL">Florida</option>
                        </select>
                      </div>
                    </div>

                    <div className="cart-form-group-row">
                      <div className="cart-form-group">
                        <input
                          type="text"
                          name="zip"
                          placeholder="ZIP Code"
                          required
                          className="cart-form-input"
                          value={formData.zip}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="cart-form-group">
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone (optional)"
                          className="cart-form-input"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="cart-summary-box">
                    <div className="cart-summary-row">
                      <span>Subtotal ({cartItem.quantity} × {cartItem.price})</span>
                      <span>${(cartItem.priceVal || 0) * (cartItem.quantity || 1)}</span>
                    </div>
                    <div className="cart-summary-row cart-summary-row--total">
                      <span>Total Amount</span>
                      <span>${(cartItem.priceVal || 0) * (cartItem.quantity || 1)}</span>
                    </div>
                  </div>

                  <div className="cart-terms-agree" style={{ fontSize: '13px', color: '#6b7280', margin: '20px 0', lineHeight: '1.5' }}>
                    By proceeding with your purchase you agree to our <a href="/terms-of-use/" style={{ color: '#ef4444', textDecoration: 'underline' }}>Terms and Conditions</a> and <a href="/privacy-policy/" style={{ color: '#ef4444', textDecoration: 'underline' }}>Privacy Policy</a>
                  </div>

                  <button type="submit" className="cart-checkout-submit-btn">
                    Pay now
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Hidden Downloadable Invoice Element */}
      {completedOrder && (
        <div id="receipt-downloadable-invoice" style={{ display: 'none', padding: '40px', fontFamily: 'Arial, sans-serif', color: '#333', backgroundColor: '#fff', width: '700px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '3px solid #5c9456', paddingBottom: '20px', marginBottom: '30px' }}>
            <div>
              <img src={`${window.location.origin}/assets/images/logo.png`} alt="Al Quran Lesson" style={{ height: '60px', width: 'auto' }} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <h1 style={{ margin: 0, color: '#5c9456', fontSize: '28px' }}>PAYMENT RECEIPT</h1>
              <p style={{ margin: '5px 0 0 0', color: '#666' }}>Receipt No: {completedOrder.receiptNo}</p>
              <p style={{ margin: '2px 0 0 0', color: '#666' }}>Date: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
            <div>
              <h3 style={{ margin: '0 0 10px 0', color: '#5c9456' }}>Customer Details</h3>
              <p style={{ margin: '3px 0' }}><strong>Name:</strong> {completedOrder.firstName} {completedOrder.lastName}</p>
              <p style={{ margin: '3px 0' }}><strong>Email:</strong> {completedOrder.email}</p>
              {completedOrder.phone && <p style={{ margin: '3px 0' }}><strong>Phone:</strong> {completedOrder.phone}</p>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#5c9456' }}>Billing Address</h3>
              <p style={{ margin: '3px 0' }}>{completedOrder.address}</p>
              <p style={{ margin: '3px 0' }}>{completedOrder.city}, {completedOrder.state} {completedOrder.zip}</p>
              <p style={{ margin: '3px 0' }}>United States</p>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '40px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f2f2f2', borderBottom: '2px solid #5c9456' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', width: '100px' }}>Quantity</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', width: '150px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '15px 12px' }}>
                  <strong>{completedOrder.packageName}</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Al-Quran Lesson Online Enrollment Package</div>
                </td>
                <td style={{ padding: '15px 12px', textAlign: 'center' }}>{completedOrder.quantity}</td>
                <td style={{ padding: '15px 12px', textAlign: 'right' }}>${completedOrder.packagePrice}</td>
              </tr>
              <tr style={{ backgroundColor: '#fafafa' }}>
                <td colSpan="2" style={{ padding: '15px 12px', textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>Total Amount Paid (USD):</td>
                <td style={{ padding: '15px 12px', textAlign: 'right', fontWeight: 'bold', fontSize: '18px', color: '#5c9456' }}>${completedOrder.totalAmount}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ borderTop: '1px solid #ddd', paddingTop: '20px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
            <p style={{ margin: '5px 0' }}>Thank you for your purchase! Our team will contact you shortly to schedule your sessions.</p>
            <p style={{ margin: '5px 0' }}>Al-Quran Lesson | info@alquranlesson.com | www.alquranlesson.com</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;

